import { Command } from 'commander';
import { readFileSync } from 'fs';
import { fileURLToPath } from 'url';
import { dirname, join, basename } from 'path';
import { CloudGridError, setVerbose, isVerbose } from './utils.js';
import { PollTimeoutError } from './services/plug-poll.js';
import { friendlyNetworkMessage } from './api/fetch-errors.js';
import { emitTerminalCloudGridError } from './handle-error.js';
import { PROGRAM_DESCRIPTION, FIRST_RUN_HELP } from './cli-copy.js';
import { checkForUpdates } from './update-check.js';
import { enforceVersionPolicy } from './version-policy.js';
import { initCommand } from './commands/init.js';
import { plugCommand } from './commands/plug.js';
import { statusCommand } from './commands/status.js';
import { followCommand } from './commands/follow.js';
import { watchCommand } from './commands/watch.js';
import { logsCommand } from './commands/logs.js';
import { openCommand } from './commands/open.js';
import { infoCommand } from './commands/info.js';
import { unplugCommand } from './commands/unplug.js';
import { linkCommand } from './commands/link.js';
import { renameCommand } from './commands/rename.js';
import { visibilityCommand } from './commands/visibility.js';
import { rollbackCommand } from './commands/rollback.js';
import { agentCommand } from './commands/agent.js';
import { versionsCommand } from './commands/versions.js';
import { downloadCommand } from './commands/download.js';
import { pickupCommand } from './commands/pickup.js';
// BL598 Phase 2 — kubectl-noun command groups.
import { getCommand } from './commands/get/index.js';
import { createCommand } from './commands/create/index.js';
import { describeCommand } from './commands/describe/index.js';
import { deleteCommand } from './commands/delete/index.js';
import { forkCommand } from './commands/fork.js';
import { sshCommand } from './commands/ssh.js';
import { envCommand } from './commands/env/index.js';
import { secretsCommand } from './commands/secrets/index.js';
import { registerWireCommand } from './commands/wire.js';
import { devCommand } from './commands/dev.js';
import { scaffoldCommand } from './commands/scaffold/index.js';
import { adminCommand } from './commands/admin/index.js';
import { spaceCommand } from './commands/space/index.js';
import { hooksCommand } from './commands/hooks.js';
import { debugCommand } from './commands/debug/index.js';
import { completionCommand } from './commands/completion.js';
import { doctorCommand } from './commands/doctor.js';
import { loginCommand } from './commands/login.js';
import { logoutCommand } from './commands/logout.js';
import { whoamiCommand } from './commands/whoami.js';
import { useCommand } from './commands/use.js';
import { feedbackCommand } from './commands/feedback.js';
import { formatVersion, getVersionInfo } from './version.js';

const __dirname = dirname(fileURLToPath(import.meta.url));
const pkg = JSON.parse(readFileSync(join(__dirname, '../package.json'), 'utf-8'));

// Decision 016 §"Privacy and anonymization" — `--version --verbose` is one
// of three discoverability surfaces for telemetry status. Pre-empt
// commander's auto-handler so we can emit the verbose telemetry block.
const argvHasVersion = process.argv.includes('--version') || process.argv.includes('-V');
if (argvHasVersion) {
  const verboseFlag = process.argv.includes('--verbose') || process.argv.includes('-v');
  process.stdout.write(formatVersion(getVersionInfo(pkg.version), verboseFlag));
  process.exit(0);
}

const program = new Command();

// `grid` is the default (ergonomic) invoker; `gridctl` is the canonical/spec
// binary (Decision 042); `cloudgrid` and `cg` remain as aliases. All four bin
// entries launch this same CLI. Derive the program name from the invoked binary
// so `--help`/usage reflects whichever name the user typed, falling back to the
// default `grid` when the invocation path is unavailable.
const invokedAs = basename(process.argv[1] || 'grid');

program
  .name(invokedAs)
  // First thing a new builder sees when they type `cloudgrid`. The old copy
  // ("in development. Most commands not yet available.") undersold a CLI that
  // now ships 40+ verbs and read as a warning. §23a: confident, concrete verb.
  .description(PROGRAM_DESCRIPTION)
  .version(pkg.version)
  .option('-v, --verbose', 'Show detailed output')
  // Required for `cloudgrid dev -- npm test` (and similar) to pass flags
  // through to the wrapped child command. The `dev` command sets
  // `passThroughOptions(true)` and commander throws on import unless the
  // parent program also has positional options enabled.
  .enablePositionalOptions(true);

// First-run orientation. `cloudgrid` with no command prints help; the lines
// in FIRST_RUN_HELP sit under the command list so a brand-new builder sees the
// path before they read 40 verbs. §23b: friendly, brief, one suggestion.
program.addHelpText('after', FIRST_RUN_HELP);

program.hook('preAction', (_thisCommand, _actionCommand) => {
  const opts = program.opts();
  if (opts.verbose) setVerbose(true);
});

program
  .command('version')
  .description('Show CLI version')
  .action(() => { process.stdout.write(`${pkg.version}\n`); });

program.addCommand(initCommand);
program.addCommand(plugCommand);
program.addCommand(statusCommand);
// Backlog 072-A/B — async deploy companions.
program.addCommand(followCommand);
program.addCommand(watchCommand, { hidden: true });
program.addCommand(logsCommand);
program.addCommand(openCommand);
program.addCommand(infoCommand);
program.addCommand(unplugCommand);
program.addCommand(linkCommand);
program.addCommand(renameCommand);
// BL598 Phase 2 — kubectl-noun groups. `get` (grids/entities/spaces),
// `create grid`, `describe` (grid/entity), and `delete` (grid/entity) replace
// the deleted top-level `list`, `builds`, and `delete` verbs (clean break per
// founder direction 2026-06-28 — no backward compatibility before public
// release).
program.addCommand(getCommand);
program.addCommand(createCommand);
program.addCommand(describeCommand);
program.addCommand(deleteCommand);
// 0.8.11 — visibility / auth-mode controls (backlog 118).
program.addCommand(visibilityCommand);
// M8b — mutating-ops verbs (spec v17 §13.5–§13.9).
program.addCommand(rollbackCommand);
program.addCommand(agentCommand);
program.addCommand(versionsCommand);
// BL598 Phase 1A — `pickup` and `download` are the canonical collaboration
// verbs (the former `clone` / `pull` were removed in Phase 2's clean break).
program.addCommand(downloadCommand);
program.addCommand(pickupCommand);
program.addCommand(forkCommand);
program.addCommand(sshCommand);
program.addCommand(envCommand);
program.addCommand(secretsCommand);
registerWireCommand(program);
program.addCommand(devCommand);
// Scaffold commands — per decision 015 §"Scaffold commands". Adds idempotent
// project re-scaffolding plus `service` / `rename` subcommands so users add
// new services without hand-editing yaml + folders.
program.addCommand(scaffoldCommand);
// M8c — admin/space subcommand groups (spec v17 §14). Source kept through
// M0 clean-slate but registration was lost; re-wiring here per backlog 058.
program.addCommand(adminCommand);
program.addCommand(spaceCommand);
// BL621 Phase 3 (Decision 042 §"Mode 6") — `hooks` is a folder-rooted
// entity-config group, top-level now (moved out of `admin`). It operates on
// the cwd entity / platform by default.
program.addCommand(hooksCommand);
// BL621 Phase 3 (Decision 042 §"Mode 4 — Debug") — the `debug` group
// (`debug call`, `debug ssh <entity>`) is developer tooling, hidden from the
// default `--help`.
program.addCommand(debugCommand, { hidden: true });
program.addCommand(completionCommand);
program.addCommand(doctorCommand);
// 0.8.4 batch — auth verbs ported back from v0.7 with v0.8 identity-model
// adjustments. Backlog 041.2 is closed once these ship.
program.addCommand(loginCommand);
program.addCommand(logoutCommand);
program.addCommand(whoamiCommand);
program.addCommand(useCommand);
// Decision 016 — `cloudgrid feedback` posts to the same telemetry pipeline
// as auto error/success events. Replaces the legacy v0.7 verb (backlog 048).
program.addCommand(feedbackCommand);
// BL621 Phase 3 (Decision 042 §"Mode 4 — Debug") — `cloudgrid call` moved into
// the hidden `debug` group as `debug call` (registered above).
// BL598 Phase 2 — brain / telemetry / errors are reached ONLY under
// `gridctl admin <command>` now. The top-level aliases (and their deprecation
// shim) were removed in the clean break; `gridctl admin hooks deliveries
// --limit 5` is the CLAUDE.md release-verification command.

if (process.argv.includes('--verbose') || process.argv.includes('-v')) {
  setVerbose(true);
}

// Backlog 200 — server-driven version policy. Runs BEFORE the friendly
// `checkForUpdates` nudge so a `block` policy short-circuits the npm-registry
// poll for users who can't continue anyway. `CLOUDGRID_SKIP_VERSION_CHECK=1`
// bypasses entirely (documented in CLOUDGRID.md + the block message).
await enforceVersionPolicy(pkg.version).catch(() => {});

await checkForUpdates(pkg.version).catch(() => {});

try {
  await program.parseAsync();
} catch (e) {
  // Backlog 544 — a poll timeout where the deploy is still progressing
  // server-side (build succeeded, or still building) is NOT a hard failure.
  // Print the softened, self-contained guidance and exit 2 (warning: didn't
  // fully verify, but nothing is broken). A genuine hard timeout still
  // exits 1 via the message + branch below.
  if (e instanceof PollTimeoutError) {
    console.error(e.softTimeout ? e.message : `Error: ${e.message}`);
    process.exit(e.softTimeout ? 2 : 1);
  }
  if (e instanceof CloudGridError) {
    console.error(`Error: ${e.message}`);
    // BL593 — a terminal CloudGridError (queued/in-process plug failure, and
    // every other verb's terminal CloudGridError) reaches here and exits
    // without ever routing through handleV2Error, so it emitted no telemetry.
    // Fire a cli_events `error` row before exiting. Best-effort, 250ms-capped,
    // swallows its own errors; awaited so the POST has a chance to complete
    // before process.exit. The printed message above is unchanged.
    await emitTerminalCloudGridError(e);
    process.exit(1);
  }
  // `cloudgrid plug` rethrows raw errors here rather than through
  // handleV2Error, so a network failure during the deploy poll lands in this
  // branch. Give it the same blameless line the other verbs get.
  const networkMsg = friendlyNetworkMessage(e);
  if (networkMsg) {
    console.error(`Error: ${networkMsg}`);
    process.exit(1);
  }
  if (isVerbose()) {
    console.error(e);
  } else if (e instanceof Error) {
    console.error(`Error: ${e.message}`);
  }
  process.exit(1);
}
